import React, { useState, useRef } from 'react';
import axios from '../../utils/axiosConfig';
import { Camera, Upload, Check, AlertCircle, Loader2 } from 'lucide-react';

const UploadAvatar = ({ client, onUpdate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', msg: string }
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setStatus(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append('file0', selectedFile);

    try {
      setLoading(true);
      const res = await axios.post(
        `/api/clients/upload-avatar/${client._id}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
        }
      );

      setStatus({ type: 'success', msg: 'Logo actualizado' });
      setSelectedFile(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', msg: 'Error al subir' });
    } finally {
      setLoading(false);
      setTimeout(() => setStatus(null), 3000);
    }
  };

  return (
    <div className="relative group flex flex-col items-center">
      {/* Contenedor del Avatar */}
      <div className="relative w-32 h-32 md:w-40 md:h-40">
        {/* Imagen Circle */}
        <div className="w-full h-full rounded-full overflow-hidden border-4 border-white/5 bg-background shadow-2xl relative">
          <img
            src={previewUrl || client.logoUrl || '/logolm.jpg'}
            alt="Logo"
            className={`w-full h-full object-cover transition-all duration-500 ${loading ? 'opacity-30 blur-sm' : 'opacity-100'}`}
          />

          {/* Overlay de carga */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Loader2 className="text-primary-orange animate-spin" size={32} />
            </div>
          )}

          {/* Trigger de input al pasar el mouse */}
          {!loading && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            >
              <Camera className="text-white mb-1" size={24} />
              <span className="text-[10px] font-black uppercase text-white tracking-widest text-center px-2">Cambiar Logo</span>
            </button>
          )}
        </div>

        {/* Badge de estado (Success/Error) */}
        {status && (
          <div className={`absolute -bottom-2 -right-2 p-2 rounded-full shadow-lg transition-all duration-500
            ${status.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
            {status.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
          </div>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Botón de confirmar subida (aparece solo si hay algo seleccionado) */}
      {selectedFile && !loading && (
        <button
          onClick={handleUpload}
          className="mt-6 flex items-center gap-2 px-6 py-2.5 bg-primary-orange text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:opacity-90 active:scale-95 transition-all shadow-xl shadow-orange-500/20 animate-fade-in-up"
        >
          <Upload size={14} />
          Confirmar Cambio
        </button>
      )}

      {/* Mensajes de error flotantes */}
      {status?.type === 'error' && (
        <p className="absolute -bottom-6 text-[10px] font-bold text-red-500 uppercase tracking-widest whitespace-nowrap animate-fade-in-up">{status.msg}</p>
      )}
    </div>
  );
};

export default UploadAvatar;
